import { useCallback, useEffect, useRef, useState } from 'react';

import type { IncidentId } from '../realtime/contracts';
import { activateChat, locateCaller } from './incidentCommands';
import type { IncidentCommandAction } from './incidentOperationText';
import { activateLiveVideo } from './liveVideoApi';

export type IncidentCommandStatus = 'idle' | 'pending' | 'failed';
export type IncidentCommand = {
  status: IncidentCommandStatus;
  start: () => Promise<boolean>;
};

const COMMANDS = { locate: locateCaller, chat: activateChat, videoChat: activateLiveVideo };

export function useIncidentCommand(
  incidentId: IncidentId,
  action: IncidentCommandAction,
  confirmedState: string | null,
): IncidentCommand {
  const [status, setStatus] = useState<IncidentCommandStatus>('idle');
  const pending = useRef<Promise<void> | null>(null);
  const stateKey = `${incidentId}:${action}:${confirmedState}`;
  const previousState = useRef(stateKey);

  useEffect(() => {
    if (previousState.current === stateKey) return;
    previousState.current = stateKey;
    pending.current = null;
    setStatus('idle');
  }, [stateKey]);

  const start = useCallback(async () => {
    if (pending.current !== null) return false;
    setStatus('pending');
    const request = COMMANDS[action](incidentId);
    pending.current = request;
    try {
      await request;
      if (action === 'locate' && pending.current === request) {
        pending.current = null;
        setStatus('idle');
      }
      return true;
    } catch {
      if (pending.current === request) {
        pending.current = null;
        setStatus('failed');
      }
      return false;
    }
  }, [incidentId, action]);

  return { status, start };
}
